import type { ReactNode } from "react";
import { AlertTriangle, Lock, RefreshCw, WifiOff } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "./Button";

/* ---------------------------------------------------------------- esqueleto */

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("block animate-pulse rounded-sm bg-line-2", className)}
    />
  );
}

/** Esqueleto con la forma de una tabla, no un spinner centrado. */
export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="divide-y divide-line-2" aria-hidden>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-3.5">
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton
              key={c}
              className={cn("h-3", c === 0 ? "w-40" : c === cols - 1 ? "ml-auto w-16" : "w-24")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------- vacios */

export function EmptyState({
  icon,
  title,
  description,
  actions,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="px-5 py-12 text-center">
      {icon && (
        <div className="mx-auto mb-3 grid size-10 place-items-center rounded-lg bg-sunk text-ink-3">
          {icon}
        </div>
      )}
      <h3 className="mb-1 text-sm font-semibold">{title}</h3>
      {description && (
        <p className="mx-auto mb-4 max-w-[38ch] text-ui text-ink-2">{description}</p>
      )}
      {actions && <div className="flex justify-center gap-2">{actions}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ errores */

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  const offline = /conexión|conexion|red/i.test(message);
  return (
    <EmptyState
      icon={offline ? <WifiOff className="size-5" /> : <AlertTriangle className="size-5" />}
      title={offline ? "Sin conexión" : "No se pudo cargar"}
      description={message}
      actions={
        onRetry && (
          <Button size="sm" variant="secondary" icon={<RefreshCw className="size-3.5" />} onClick={onRetry}>
            Reintentar
          </Button>
        )
      }
    />
  );
}

export function ForbiddenState({ what = "esta sección" }: { what?: string }) {
  return (
    <EmptyState
      icon={<Lock className="size-5" />}
      title="Tu rol no incluye este acceso"
      description={`No tienes permiso para ver ${what}. Pídeselo a quien administre la tienda.`}
    />
  );
}
